import { Dictionary } from "highcharts";
import { IFacetedFilterSelection, ISelectionInfo } from "../hy-filter-panel-module/types";
import { OptionModel } from "../models/models";
import { ValidateImportErrorModel, ExportConfigurationsWithDependenciesModel } from "./configuration-common.models";
import { ITranslationService } from "./models-index";
import { RuleModel } from "./data-set-configuration-models";

export interface GetReportViewConfigurationRequest {
  pageIndex: number;
  pageSize: number;
  sortField?: string;
  ascending: boolean;
  searchTerm?: string;
}

export interface GetReportViewConfigurationResponse {
  reportViewConfigurations: ReportViewConfigurationResponse[];
  totalCount: number;
  page: number;
}

export interface CreateReportViewConfigurationRequest {
  name: string;
  title?: string;
  description?: string;
  filters: ReportFilterConfiguration[];
  panels: Panel[];
  perspectives: ReportViewPerspective[];
  tags: string[];
  comment?: string;
}



export interface ReportViewConfigurationResponse {
  id: string;
  name: string;
  title?: string;
  description?: string;
  created: Date;
  filters: ReportFilterConfiguration[];
  panels: Panel[];
  perspectives: ReportViewPerspective[];
  tags: string[];
}

export interface ReportViewConfigurationListResponse {
  id: string;
  name: string;
  title?: string;
  description?: string;
  created: Date;
  modified?: Date;
  tags?: string[];
}

export type ReportFilterSimpleValue = { value: string | number; display: string; path?: string }
export type ReportFilterHierarchyValue = { name: string; key: string; value: string, path: string, hierarchyTypeId?: string, hierarchyTypeName?: string };
export type ReportFilterDateRangeValue = { name?: string; startDate: string; endDate: string, path?: string, kind?: string; };
export type ReportFilterOperatorValue = { operator: string; operand: string | number | Date; }

export type ReportFilterValue = string | string[] | ReportFilterSimpleValue | ReportFilterHierarchyValue | ReportFilterDateRangeValue | ReportFilterDateRangeValue[] | ReportFilterOperatorValue;


export type ReportFilterItemValueType = "string" | "simple-value" | "hierarchy-value" | "date-range-value";

export interface ReportFilterItem {
  name: string;
  label?: string;
  type: string;
  filterType?: string;
  path: string;
  value?: ReportFilterValue;
  valueType?: ReportFilterItemValueType;
  searchable: boolean;
  items?: ReportFilterItem[];
}

export class ReportViewFilterConfiguration {
  reportName: string;
  filterNames: string[];
}

export type PerspectiveSelectorType = "Panel" | "Element" | "Filter";

export class PerspectiveSelector {
  type: PerspectiveSelectorType;
  path: string;
  items: string[];
  properties?: { [key: string]: string };
  defaultItem?: string;
}

export class ReportViewPerspective {
  name: string;
  key: string;
  description?: string;
  selectors: PerspectiveSelector[];
}

export interface ReportViewFilter {
  name: string;
  label: string;
  type: string;
  items: ReportFilterItem[];
  defaults?: ISelectionInfo;
  locked?: boolean;
  lockable?: boolean;
  removeable: boolean;
  visible: boolean;
}

export class ReportViewFilterSelection {
  name: string;
  label: string;
  item: IFacetedFilterSelection;
  locked: boolean;
  lockable: boolean;
  removeable: boolean;
  visible: boolean = true;
}

export const buildReportViewFilterSelection: (
  name: string,
  label: string,
  item: IFacetedFilterSelection,
  locked: boolean,
  lockable: boolean,
  removeable: boolean,
  visible: boolean
) => ReportViewFilterSelection = (
  name: string,
  label: string,
  item: IFacetedFilterSelection,
  locked: boolean,
  lockable: boolean,
  removeable: boolean,
  visible: boolean
) => {
  return {
    name,
    label,
    item,
    locked,
    lockable,
    removeable,
    visible
  };
};

export interface ReportFilterRequestSelection {
  name: string;
  value: ReportFilterValue;
}

// Selected* filter models (high-fidelity, multi-select friendly, path-preserving)
export interface SelectedFilterItem {
  name: string;
  type: string;
  path: string;
  label?: string;
}

export interface SelectedDateRangeFilterModel {
  item: SelectedFilterItem;
  dateRange: ReportFilterDateRangeValue; // { name, path, startDate, endDate, kind? }
}

export interface SelectedHierarchyFilterModel {
  item: SelectedFilterItem;
  hierarchies: ReportFilterHierarchyValue[]; // array supports multi-select
}

export interface SelectedFacetFilterModel {
  item: SelectedFilterItem;
  facets: { name: string; path: string; value?: string; label?: string }[]; // FacetFilterValue shape
}

export type SelectedFilterModel = SelectedDateRangeFilterModel | SelectedHierarchyFilterModel | SelectedFacetFilterModel;

export interface ReportViewResponse {
  id: string;
  reportName: string;
  filters: ReportFilterConfiguration[];
  dataSets: DataSet[];
  panels: Panel[];
  perspectives: ReportViewPerspective[];
  title?: string;
}

export interface ViewContext {
  [key: string]: any;
  reportName: string;
  selectedPerspective?: ReportViewPerspective;
  state: Dictionary<string>;
};

export interface PanelComponent {
  panelConfig: Panel;
  dataSets: DataSet[];
  rowPanelId: string;
  translationService?: ITranslationService;
  viewContext?: ViewContext;
  perspective?: string;
}

export interface DataSet {
  name: string;
  rows: Array<RowValue[]>;
  columns: Column[];
  columnSets: ColumnSet[];
}

export type ColumnTypes = "dimension" | "metric" | "meta";

export interface Column {
  displayName: string;
  name: string;
  type: ColumnTypes;
  show: boolean;
  export?: boolean;
  sortable?: boolean;
  valueInverted?: boolean;
}

export interface RowValue {
  label?: string;
  value?: string | Date | number;
  formatter?: string;
}

export interface ColumnSet {
  name: string;
  dataSet?: string;
  displayName?: string;
  columnNames: string[];
  perspective?: string;
}

export interface DefaultFilterValueConfiguration {
  value: string;
}

export interface ReportFilterConfiguration {
  name: string;
  label: string;
  type: string;
  query: string;
  loadMode: string;
  defaults: DefaultFilterValueConfiguration[];
  options?: any;
  locked: boolean;
  lockable: boolean;
  removeable: boolean;
  visible: boolean;
}

export interface PanelConfiguration {
  name: string;
  label?: string;
  elements: Element[];
  options?: OptionModel[];
  helpTextTitle?: string;
  helpTextKey?: string;
  titleLinkUrl?: string;
  titleLinkUrlFilters?: PanelFilterValue[];
  disableMetricSelection?: boolean;
  htmlContent?: string;
  perspective?: string;
}

export interface Panel {
  name: string;
  type: string;
  configurations: PanelConfiguration[];
  row?: number;
  widthUnits?: number;
  widthBasis?: number;
  disableExpand?: boolean;
  indexOrdering: IndexOrderingItem[];
  translationService?: ITranslationService;
  perspective?: string;
}

export interface IndexOrderingItem {
  label?: string;
  value: string;
  children: IndexOrderingItem[];
  rules: RuleModel[];
}

export interface Element {
  name: string;
  type: string;
  settings: ElementSettings;
  widthUnits?: number;
  widthBasis?: number;
  perspective?: string;
}

export interface ElementComponent {
  dataSets: DataSet[];
  settings: ElementSettings;
  viewContext?: ViewContext;
  panelConfiguration?: PanelConfiguration;
  selectedMetric?: string;
  selectedMetrics?: string[];
  // options like POP, YOY, heatmap in table header.
  options?: OptionModel[];
}

export interface ElementSettings {
  dataSet?: string;
  dataSets?: string[];
  metrics?: ElementMetric[];
  secondaryMetricName?: string;
  secondaryMetricTrend?: string;
  color?: string;
  dimensionName?: string;
  metricName?: string;
  chartColors?: string[];
  chartMetrics?: string[];
  title?: string;
  metricTrend?: string;
  metricNamePreviousPeriod?: string;
  metricNamePreviousPTD?: string;
  metricNamePreviousYear?: string;
  metricNamePOP?: string;
  metricNameYOY?: string;
  metricInverted?: boolean;
  titleIconClasses?: string;
  widthUnits?: number;
  widthBasis?: number;
  showPreviousPTD?: boolean;
  helpTextKey?: string;
  helpTextTitle?: string;
  colorPropertyName?: string;
  xAxisTitle?: string;
  yAxisTitle?: string;
  chartPrimaryColor?: string;
  chartSecondaryColor?: string;
  dimensionRowFilters?: string[];
  dimensionNames?: string[];
  defaultMetrics?: string[];
  columnSetNames?: string[];
  shareYAxis?: boolean;
  options: [keyof string, string][];
}

export interface ElementMetric {
  name: string;
  metricInverted: boolean;
  trend?: string;
  scenario?: string;
  color?: string;
  colorPropertyName?: string;
  pop?: string;
  yoy?: string;
  previousPeriod?: string;
  previousYear?: string;
  title?: string;
  titleIconClasses?: string;
  helpTextKey?: string;
  helpTextTitle?: string;
  perspective?: string;
}

export interface PanelFilterValue {
  type: string;
  values?: string;
}

export interface IRowPanel {
  rowIndex: number;
}

export type ExportReportViewConfigurationResponse = ExportConfigurationsWithDependenciesModel;

export type ExportReportViewConfigurationWithDependenciesResponse = ExportConfigurationsWithDependenciesModel;

export interface ExportReportViewConfigurationModel {
  name: string;
  type: string;
  statement: string;
  filters?: string;
  panels?: Panel[];
  tags?: string[];
}

export interface ImportReportViewConfigurationRequest {
  key: string;
  queryNames: string[];
  dataSetNames: string[];
  reportViewNames: string[];
  filterNames: string[];
}

export interface ValidateExportReportViewsResponse {
  key: string;
  reportViews: ExportItemResponse[];
  dataSets: ExportItemResponse[];
  queries: ExportItemResponse[];
  filters: ExportItemResponse[];
}

export interface ExportItemResponse {
  name: string;
}

export interface ExportReportViewFromSelectionRequest {
  key: string;
  queryNames: string[];
  dataSetNames: string[];
  reportViewNames: string[];
  filterNames: string[];
}

export interface ValidateImportReportViewsResponse {
  key: string;
  reportViews: ValidateImportReportViewsResponseModel[];
  dataSets: ValidateImportReportViewsResponseModel[];
  queries: ValidateImportReportViewsResponseModel[];
  filters: ValidateImportReportViewsResponseModel[];
}

export interface ValidateImportReportViewsResponseModel {
  name: string;
  status: string;
  error?: ValidateImportErrorModel | null;
}
